import { useEffect, useState } from "react";

/**
 * True when the viewport is at/above the DESKTOP breakpoint (64rem = 1024px) — the
 * only width at which the docked "Messaging" widget renders. Below it the dock is
 * suppressed entirely and the mobile messaging experience stays the AppBar
 * Messages action → the full-page /community/messages surface.
 *
 * Uses `matchMedia` so the dock appears/disappears on a live resize across the
 * breakpoint. When `matchMedia` is unavailable (SSR / jsdom under test) it defaults
 * to FALSE — the dock stays HIDDEN — which is the safe default for a brand-new
 * overlay surface (an unknown environment shows nothing rather than mounting a
 * network-touching widget) and keeps it out of every existing shell/app test that
 * does not opt in. A real browser always has `matchMedia`, so this fallback is
 * test-only; a dock test drives it by stubbing `window.matchMedia` to desktop.
 *
 * The `social` module owns this copy (it cannot import booking's `useIsNarrow` —
 * module isolation) and uses the dock's own 64rem query, distinct from the messages
 * two-pane 860px breakpoint.
 */
const DESKTOP_QUERY = "(min-width: 64rem)";

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}
